import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useBMP } from '../context/BMPContext';
import useMarketNotifications from '../hooks/useMarketNotifications';
import { useTranslation } from 'react-i18next';
import './BMPHeader.css';

export default function BMPHeader({ onExit, onToggleSidebar }) {
  const { t } = useTranslation();
  const { profile, bmpNavigate, userInfo } = useBMP();
  const { notifications, unreadCount, fetchNotifications, fetchUnreadCount, markAllRead } = useMarketNotifications();
  const [searchQ, setSearchQ] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const menuRef = useRef(null);
  const notifRef = useRef(null);

  // Fetch notifications count periodically
  useEffect(() => {
    if (profile) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [profile, fetchUnreadCount]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    if (menuOpen || notifOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen, notifOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    bmpNavigate('catalog', { q: searchQ.trim() });
  };

  const menuAction = (view) => {
    setMenuOpen(false);
    if (view === 'exit') { onExit(); return; }
    bmpNavigate(view);
  };

  const handleOpenNotifs = useCallback(() => {
    setNotifOpen(v => !v);
    if (!notifOpen) {
      fetchNotifications();
      markAllRead();
    }
  }, [notifOpen, fetchNotifications, markAllRead]);

  return (
    <header className="bmp-header">
      <div className="bmp-header-left">
        <button className="bmp-header-menu" onClick={onToggleSidebar} aria-label="Menu">
          &#9776;
        </button>
        <button className="bmp-header-back" onClick={onExit}>
          &#8592; BanaPrecios
        </button>
        <div className="bmp-header-brand" onClick={() => bmpNavigate('catalog')}>
          <span className="bmp-logo-text">Bana</span>
          <span className="bmp-logo-accent">MarketPlace</span>
        </div>
      </div>

      <form className="bmp-header-search" onSubmit={handleSearch}>
        <input
          type="text"
          placeholder={t('bmp_search_placeholder')}
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
        />
        <button type="submit" className="bmp-search-btn">&#128269;</button>
      </form>

      <div className="bmp-header-right">
        {profile && (
          <span className={`bmp-header-role bmp-role--${profile.role}`}>
            {profile.role_display}
          </span>
        )}

        {/* Notifications bell */}
        {profile && (
          <div className="bmp-notif-wrapper" ref={notifRef}>
            <button className="bmp-header-bell" onClick={handleOpenNotifs}>
              &#128276;
              {unreadCount > 0 && <span className="bmp-bell-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
            </button>
            {notifOpen && (
              <div className="bmp-notif-dropdown">
                <div className="bmp-notif-header">
                  <strong>{t('bmp_notifications')}</strong>
                </div>
                {notifications.length === 0 ? (
                  <p className="bmp-notif-empty">{t('bmp_no_notifications')}</p>
                ) : (
                  <div className="bmp-notif-list">
                    {notifications.slice(0, 15).map(n => (
                      <div key={n.id} className={`bmp-notif-item${n.is_read ? '' : ' bmp-notif-item--unread'}`}
                        onClick={() => {
                          setNotifOpen(false);
                          if (n.bid) bmpNavigate('received_bids');
                          else if (n.transaction) bmpNavigate('transactions');
                        }}
                      >
                        <span className="bmp-notif-title">{n.title}</span>
                        {n.message && <span className="bmp-notif-msg">{n.message}</span>}
                        <span className="bmp-notif-time">{new Date(n.created_at).toLocaleDateString('es-EC')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Avatar menu */}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            className={`bmp-header-avatar${menuOpen ? ' bmp-header-avatar--active' : ''}`}
            onClick={() => setMenuOpen(v => !v)}
            title={userInfo?.username}
          >
            {userInfo?.photo_url
              ? <img src={userInfo.photo_url} alt="" />
              : <span>{(userInfo?.username || '?')[0].toUpperCase()}</span>
            }
          </button>

          {menuOpen && (
            <div className="bmp-dropdown">
              <div className="bmp-dropdown-user">
                <strong>{userInfo?.full_name || userInfo?.username}</strong>
                <span>@{userInfo?.username}</span>
                {profile && <span className="bmp-dropdown-role">{profile.role_display} {profile.is_verified ? '✓' : ''}</span>}
                {profile?.rating_avg && (
                  <span className="bmp-dropdown-rating">{'★'.repeat(Math.round(profile.rating_avg))} {profile.rating_avg}/5 ({profile.rating_count})</span>
                )}
              </div>
              <div className="bmp-dropdown-sep" />
              <button className="bmp-dropdown-item" onClick={() => menuAction('profile')}>
                <span className="bmp-dropdown-icon">&#9881;</span>
                {t('bmp_menu_profile')}
              </button>
              <button className="bmp-dropdown-item" onClick={() => menuAction('analytics')}>
                <span className="bmp-dropdown-icon">&#128200;</span>
                {t('bmp_menu_analytics')}
              </button>
              <button className="bmp-dropdown-item" onClick={() => menuAction('settings')}>
                <span className="bmp-dropdown-icon">&#128274;</span>
                {t('bmp_menu_security')}
              </button>
              {profile?.role === 'productor' && (
                <button className="bmp-dropdown-item" onClick={() => menuAction('farms')}>
                  <span className="bmp-dropdown-icon">&#127793;</span>
                  {t('bmp_my_farms')}
                </button>
              )}
              <button className="bmp-dropdown-item" onClick={() => menuAction('transactions')}>
                <span className="bmp-dropdown-icon">&#128203;</span>
                {t('bmp_transactions')}
              </button>
              <div className="bmp-dropdown-sep" />
              <button className="bmp-dropdown-item bmp-dropdown-item--exit" onClick={() => menuAction('exit')}>
                <span className="bmp-dropdown-icon">&#8592;</span>
                {t('bmp_menu_exit')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
